import React, { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';

import { Button } from '@ui/components/ui/button';
import { Input } from '@ui/components/ui/input';
import { cardClass } from '@app/constants/styles';
import { EditSheet } from '@app/components/core/edit-sheet';
import { useIsMobile } from '@ui/hooks/use-mobile';
import { useHourlyCost, useSetHourlyCost } from '@data/hooks/membership';

import { asAmount, formatEur } from '../../products/productDetail/budget';

/** Its own card, not a row of the details: admins and PMs both edit it, and only admins edit the rest. */
const MemberHourlyCost = ({ membershipId }: { membershipId: string }): ReactElement => {
  const { t } = useTranslation();
  const { data } = useHourlyCost(membershipId);
  const save = useSetHourlyCost(membershipId);
  const [draft, setDraft] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const isMobile = useIsMobile();

  const stored = data?.hourlyCost;
  const typed = draft ?? '';
  const amount = typed.trim() === '' ? null : asAmount(typed);
  const invalid = typed.trim() !== '' && amount === null;

  const submit = (): void => {
    if (invalid) return;
    setFailed(false);
    save.mutate(amount, { onSuccess: () => setDraft(null), onError: () => setFailed(true) });
  };

  // A phone edits in a sheet (below), leaving the card as it reads.
  const inline = draft !== null && !isMobile;
  const field = (
    <div className="flex items-center gap-1.5">
      <span className="text-sm text-muted-foreground">€</span>
      <Input
        aria-label={t('memberDetails.hourlyCost')}
        inputMode="decimal"
        value={draft ?? ''}
        autoFocus
        onChange={event => setDraft(event.target.value)}
        aria-invalid={invalid}
        className="w-32 bg-popover text-right tabular-nums"
      />
      <span className="text-sm text-muted-foreground">{t('memberDetails.perHour')}</span>
    </div>
  );

  return (
    <section className={`${cardClass} mb-6`}>
      <div className="mb-3 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-foreground">{t('memberDetails.hourlyCost')}</h2>
          <p className="text-sm text-muted-foreground">{t('memberDetails.hourlyCostHint')}</p>
        </div>
        {!inline ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setDraft(stored === undefined ? '' : String(stored))}
          >
            <Pencil className="size-4" />
            {t('edit')}
          </Button>
        ) : null}
      </div>

      {!inline ? (
        <p className="text-sm tabular-nums text-foreground">
          {stored === undefined ? (
            <span className="text-muted-foreground">{t('memberDetails.noHourlyCost')}</span>
          ) : (
            t('memberDetails.hourlyCostValue', { amount: formatEur(stored) })
          )}
        </p>
      ) : (
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={event => {
            event.preventDefault();
            submit();
          }}
        >
          {field}
          <Button type="button" variant="outline" size="sm" onClick={() => setDraft(null)} disabled={save.isPending}>
            {t('actions.cancel')}
          </Button>
          <Button type="submit" size="sm" disabled={invalid || save.isPending}>
            {t('actions.save')}
          </Button>
        </form>
      )}
      {inline && invalid ? (
        <p className="mt-2 text-sm text-destructive">{t('memberDetails.hourlyCostInvalid')}</p>
      ) : null}
      {inline && failed ? <p className="mt-2 text-sm text-destructive">{t('memberDetails.saveFailed')}</p> : null}
      <EditSheet
        open={draft !== null && isMobile}
        title={t('memberDetails.hourlyCost')}
        onCancel={() => setDraft(null)}
        onSubmit={event => {
          event.preventDefault();
          submit();
        }}
        isPending={save.isPending}
        canSave={!invalid}
        error={(invalid && t('memberDetails.hourlyCostInvalid')) || (failed && t('memberDetails.saveFailed')) || null}
      >
        {field}
      </EditSheet>
    </section>
  );
};

export default MemberHourlyCost;
